/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, type FocusEvent, type MouseEvent, type PointerEvent } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { preloadDynamicImage } from '@/components/dynamic-image/preload';
import type { AppConfig } from '@/types/config';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import type { GalleryImage } from './types';

export interface UseGallerySelectionOptions {
    images: GalleryImage[];
    onSelect: (index: number) => void;
    /** Widths used to preload the *main* image variant a thumbnail targets (not the thumbnail's own size). */
    preloadWidths: DynamicImageDimensions;
}

export interface UseGallerySelectionResult {
    handleThumbnailClick: (event: MouseEvent<HTMLButtonElement>) => void;
    handleThumbnailIntent: (event: PointerEvent<HTMLButtonElement> | FocusEvent<HTMLButtonElement>) => void;
    preloadSlide: (image: GalleryImage | undefined) => void;
}

/**
 * Shared thumbnail click/hover-intent handlers and main-image preloading, extracted so both `ImageGallery`'s
 * own thumbnail rendering and the standalone `ImageGalleryThumbs` (used when a layout needs the thumbnail row
 * detached from the main image, e.g. the bundle PDP) drive the same selection behavior without duplicating it.
 */
export function useGallerySelection({
    images,
    onSelect,
    preloadWidths,
}: UseGallerySelectionOptions): UseGallerySelectionResult {
    const config = useConfig<AppConfig>();

    const preloadSlide = useCallback(
        (image: GalleryImage | undefined) => {
            if (!image) {
                return;
            }
            preloadDynamicImage({
                config,
                src: image.src,
                widths: preloadWidths,
                fetchPriority: 'low',
            });
        },
        [config, preloadWidths]
    );

    const handleThumbnailIntent = useCallback(
        (event: PointerEvent<HTMLButtonElement> | FocusEvent<HTMLButtonElement>) => {
            const index = Number(event.currentTarget.dataset.index);
            if (Number.isNaN(index)) {
                return;
            }
            preloadSlide(images[index]);
        },
        [images, preloadSlide]
    );

    const handleThumbnailClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            const nextIndex = Number(event.currentTarget.dataset.index);
            if (!Number.isNaN(nextIndex)) {
                onSelect(nextIndex);
            }
        },
        [onSelect]
    );

    return { handleThumbnailClick, handleThumbnailIntent, preloadSlide };
}
